// frontend/src/pages/LandingPage/DemonstracaoPage.jsx
// Página de Captação de Leads - Formulário de Demonstração
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
    User, 
    Mail, 
    Phone, 
    Building2,
    Users,
    CheckCircle2,
    ArrowRight
} from 'lucide-react';
import Footer from '../../components/LandingPage/Footer';
import PrimaryButton from '../../components/Common/PrimaryButton';
import { sendDemoRequest } from '../../api/contactApi';

const DemonstracaoPage = () => {
    const navigate = useNavigate();
    
    // Garante que a página sempre comece no topo ao carregar
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, []);

    const [formData, setFormData] = useState({
        nome_completo: '',
        email: '',
        telefone: '',
        nome_lar: '',
        quantidade_residentes: '',
        botcheck: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Validação de email profissional (opcional, mas recomendado)
    const isValidProfessionalEmail = (email) => {
        if (!email) return true; // Se vazio, a validação required cuida
        const professionalDomains = ['gmail.com', 'hotmail.com', 'yahoo.com', 'outlook.com'];
        const domain = email.split('@')[1]?.toLowerCase();
        return !professionalDomains.includes(domain);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setIsSubmitting(true);

        // Validações
        if (!formData.nome_completo.trim()) {
            setError('Nome completo é obrigatório.');
            setIsSubmitting(false);
            return;
        }

        if (!formData.email.trim()) {
            setError('E-mail é obrigatório.');
            setIsSubmitting(false);
            return;
        }

        // Validação básica de email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Por favor, insira um e-mail válido.');
            setIsSubmitting(false);
            return;
        }

        // Aviso (não bloqueia) sobre email profissional
        if (!isValidProfessionalEmail(formData.email)) {
            console.warn('Email pessoal detectado - pode ser menos qualificado');
        }

        try {
            await sendDemoRequest({
                nome: formData.nome_completo.trim(),
                email: formData.email.trim().toLowerCase(),
                telefone: formData.telefone.trim(),
                nomeLar: formData.nome_lar.trim(),
                residentes: formData.quantidade_residentes,
                origem: window.location.href,
                botcheck: formData.botcheck
            });

            navigate('/obrigado', {
                state: {
                    nome: formData.nome_completo,
                    email: formData.email,
                    sucesso: true
                }
            });
        } catch (err) {
            console.error('[DemonstracaoPage] Erro ao enviar demonstração:', err);
            setError('Ocorreu um erro ao processar sua solicitação. Por favor, tente novamente ou entre em contato conosco.');
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-gray-50">
            {/* Conteúdo Principal */}
            <main className="py-8 sm:py-12">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    
                    {/* Cabeçalho */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-8"
                    >
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 mb-3">
                            Solicite Sua{' '}
                            <span className="bg-gradient-to-r from-emerald-600 to-emerald-500 bg-clip-text text-transparent">
                                Demonstração Gratuita
                            </span>
                        </h1>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Veja como o Higeia pode transformar a gestão do seu Lar de Idosos. 
                            Nossa equipe entrará em contato em até 24 horas para agendar sua sessão personalizada.
                        </p>
                    </motion.div>

                    {/* Formulário */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                        className="bg-white rounded-3xl p-6 sm:p-8 shadow-2xl border border-gray-100"
                    >
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <input
                                type="text"
                                name="botcheck"
                                value={formData.botcheck}
                                onChange={handleChange}
                                tabIndex={-1}
                                autoComplete="off"
                                className="hidden"
                                aria-hidden="true"
                            />
                            {/* Mensagem de Erro */}
                            {error && (
                                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-800 text-sm">
                                    {error}
                                </div>
                            )}

                            {/* Nome Completo */}
                            <div>
                                <label htmlFor="nome_completo" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                    <User className="h-4 w-4 text-emerald-600" />
                                    Nome Completo <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="nome_completo"
                                    name="nome_completo"
                                    value={formData.nome_completo}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                                    placeholder="Seu nome completo"
                                />
                            </div>

                            {/* Email Profissional */}
                            <div>
                                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                    <Mail className="h-4 w-4 text-emerald-600" />
                                    E-mail Profissional <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                                    placeholder="seu@email.com.br"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Preferimos e-mails corporativos para um contato mais profissional
                                </p>
                            </div>

                            {/* Telefone (WhatsApp) */}
                            <div>
                                <label htmlFor="telefone" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                    <Phone className="h-4 w-4 text-emerald-600" />
                                    Telefone/WhatsApp <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="tel"
                                    id="telefone"
                                    name="telefone"
                                    value={formData.telefone}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                                    placeholder="(00) 00000-0000"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Utilizamos WhatsApp para agilizar o contato
                                </p>
                            </div>

                            {/* Nome do Lar de Idosos */}
                            <div>
                                <label htmlFor="nome_lar" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                    <Building2 className="h-4 w-4 text-emerald-600" />
                                    Nome do Lar de Idosos (ILPI)
                                    <span className="text-gray-400 text-xs font-normal">(opcional)</span>
                                </label>
                                <input
                                    type="text"
                                    id="nome_lar"
                                    name="nome_lar"
                                    value={formData.nome_lar}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                                    placeholder="Ex: Lar São José"
                                />
                            </div>

                            {/* Quantidade de Residentes/Leitos */}
                            <div>
                                <label htmlFor="quantidade_residentes" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                    <Users className="h-4 w-4 text-emerald-600" />
                                    Quantos Residentes/Leitos Vocês Têm?
                                    <span className="text-gray-400 text-xs font-normal">(opcional)</span>
                                </label>
                                <input
                                    type="number"
                                    id="quantidade_residentes"
                                    name="quantidade_residentes"
                                    value={formData.quantidade_residentes}
                                    onChange={handleChange}
                                    min="1"
                                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                                    placeholder="Ex: 30"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Este dado nos ajuda a personalizar a demonstração
                                </p>
                            </div>

                            {/* Botão de Envio */}
                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className="pt-3"
                            >
                                <PrimaryButton
                                    type="submit"
                                    disabled={isSubmitting}
                                    variant="primary"
                                    size="lg"
                                    className="w-full py-3 text-base font-bold shadow-xl"
                                    icon={isSubmitting ? null : <ArrowRight className="h-5 w-5" />}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="inline-block animate-spin mr-2">⏳</span>
                                            Processando...
                                        </>
                                    ) : (
                                        'Solicitar Minha Demonstração Gratuita'
                                    )}
                                </PrimaryButton>
                            </motion.div>

                            {/* Aviso de Privacidade */}
                            <p className="text-xs text-center text-gray-500 pt-2">
                                Ao enviar este formulário, você concorda em receber contato da nossa equipe. 
                                Seus dados estão seguros e não serão compartilhados com terceiros.
                            </p>
                        </form>
                    </motion.div>

                    {/* Benefícios Rápidos */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                        className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4"
                    >
                        <div className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100 text-center">
                            <CheckCircle2 className="h-7 w-7 text-emerald-600 mx-auto mb-2" />
                            <h3 className="font-bold text-gray-900 mb-1 text-sm">Demonstração Personalizada</h3>
                            <p className="text-xs text-gray-600">
                                Sessão focada nas necessidades específicas do seu lar
                            </p>
                        </div>
                        <div className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100 text-center">
                            <CheckCircle2 className="h-7 w-7 text-emerald-600 mx-auto mb-2" />
                            <h3 className="font-bold text-gray-900 mb-1 text-sm">Sem Compromisso</h3>
                            <p className="text-xs text-gray-600">
                                Entenda como o sistema funciona antes de decidir
                            </p>
                        </div>
                        <div className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100 text-center">
                            <CheckCircle2 className="h-7 w-7 text-emerald-600 mx-auto mb-2" />
                            <h3 className="font-bold text-gray-900 mb-1 text-sm">Atendimento Especializado</h3>
                            <p className="text-xs text-gray-600">
                                Equipe preparada para responder todas suas dúvidas
                            </p>
                        </div>
                    </motion.div>
                </div>
            </main>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default DemonstracaoPage;

